
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UserCompleteProfile {
  // Profile fields
  id?: string;
  email?: string;
  display_name?: string;
  address?: string;
  location?: string;
  photo_url?: string;
  about_me?: string;
  is_verified?: boolean;
  tos_accepted?: boolean;
  // Phone from notification preferences
  phone?: string;
  // Notification preferences
  notification_email?: string;
  sms_booking_updates?: boolean;
  sms_new_bookings?: boolean;
  sms_reviews?: boolean;
  email_booking_updates?: boolean;
  email_new_bookings?: boolean;
  email_reviews?: boolean;
}

export const useUserCompleteProfile = () => {
  const [profile, setProfile] = useState<UserCompleteProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserCompleteProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setProfile(null);
          return;
        }

        // Fetch profile data
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          setError('Failed to fetch profile data');
          return;
        }

        // Fetch notification preferences
        const { data: preferencesData, error: preferencesError } = await supabase
          .from('notification_preferences')
          .select('*')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (preferencesError) {
          console.error('Error fetching notification preferences:', preferencesError);
          // Don't treat this as a fatal error since preferences might not exist yet
        }

        // Combine the data
        const combinedProfile: UserCompleteProfile = {
          ...profileData,
          phone: preferencesData?.notification_phone,
          notification_email: preferencesData?.notification_email,
          sms_booking_updates: preferencesData?.sms_booking_updates,
          sms_new_bookings: preferencesData?.sms_new_bookings,
          sms_reviews: preferencesData?.sms_reviews,
          email_booking_updates: preferencesData?.email_booking_updates,
          email_new_bookings: preferencesData?.email_new_bookings,
          email_reviews: preferencesData?.email_reviews,
        };

        setProfile(combinedProfile);
      } catch (error) {
        console.error('Error in fetchUserCompleteProfile:', error);
        setError('Failed to fetch user data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserCompleteProfile();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchUserCompleteProfile();
    });

    return () => subscription.unsubscribe();
  }, []);

  return { profile, isLoading, error, refetch: () => window.location.reload() };
};
